"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sheetName = exports.xlsxVersion = exports.version = exports.optional = exports.notes = exports.location = exports.standard = exports.qrData = exports.protocol = exports.meltProtocol = exports.pcrProtocol = exports.duration = exports.rawData = exports.baselineData = exports.results = exports.fluorophores = exports.wellInfo = exports.header = void 0;
const js_sha256_1 = require("js-sha256");
const js_base64_1 = require("js-base64");
const utils_1 = require("../../utils");
const constants_1 = require("../constants");
const MAX_TARGETS = 27;
/**
 * create the header section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function header(item) {
    let headerSection = []; // Holder of all rows
    const { run, user } = item;
    headerSection.push(["Run Name", run.name]);
    headerSection.push(["Date & Time", run.timestamp]);
    headerSection.push(user.organization ? ["Organization", user.organization] : [null]);
    headerSection.push(["Team", user.team]);
    headerSection.push(["User", user.username]);
    return headerSection;
}
exports.header = header;
/**
 * create the wellInfo section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function wellInfo(item) {
    let { targets } = item.run;
    let wellNums = ["Well"];
    let channels = ["Channel"];
    let targetNames = ["Target ID"];
    let sampleNames = ["Sample ID"];
    let sampleTypes = ["Sample Type"];
    let thresholds = ["Threshold"];
    let cqs = [];
    item.protocol.meltDuration ? cqs.push("Melt Peak") : cqs.push("Cq");
    targets.map((target) => {
        wellNums.push((target.wellNumber + 1).toString() || "");
        channels.push(target.color || "");
        targetNames.push(target.name || "");
        sampleNames.push(target.sampleName || "");
        sampleTypes.push(target.sampleUnit
            ? `${target.sampleType} - ${target.sampleAmount}${target.sampleUnit}`
            : "");
        thresholds.push({
            v: Math.round(target.threshold * Math.pow(10, 6)) / Math.pow(10, 6) || 0,
            z: constants_1.THRESHOLD_FORMAT,
        });
        target.peak
            ? cqs.push({
                v: target.peak || 0,
                z: constants_1.PEAK_FORMAT,
            })
            : cqs.push({
                v: Math.round(target.cq * Math.pow(10, 6)) / Math.pow(10, 6) || 0,
                z: constants_1.CQ_FORMAT,
            });
    });
    return [
        wellNums,
        channels,
        sampleTypes,
        sampleNames,
        targetNames,
        cqs,
        thresholds,
    ];
}
exports.wellInfo = wellInfo;
/**
 * create the flurophores section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function fluorophores(item) {
    // MAP protocol targets to get each excitation color and flurophore pair
    if (item.run.sku) {
        let fluorophores = {};
        item.run.targets.forEach((item) => {
            if (!(item.color in fluorophores)) {
                fluorophores[item.color] = item.fluorophore;
            }
        });
        let response = [["Channel"], ["Fluorophore"]];
        Object.keys(fluorophores).forEach((key) => {
            response[0].push(`${key.slice(0, 1).toUpperCase()}${key.slice(1)}`);
            response[1].push(fluorophores[key]);
        });
        return response;
    }
    else {
        return [[null], [null]];
    }
}
exports.fluorophores = fluorophores;
/**
 * create the results section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function results(item) {
    let { run } = item;
    if (run.result) {
        let runResults = [];
        runResults.push(["Summary Results", run.result.overallResult.result]);
        runResults.push([null]);
        let targetResults = ["Target Result"];
        let startingQuantities = [
            "Starting Quantity",
        ];
        run.result.targetResults.forEach((targetResult) => {
            let target = run.targets.find((target) => target.wellNumber == targetResult.well &&
                target.color == targetResult.emissionColor);
            targetResults.push(targetResult ? targetResult.result : "");
            let displaySq = target ? target.sq : "";
            if (target && target.sampleType) {
                displaySq =
                    target.sourceStartingQuantity !== undefined
                        ? target.sourceStartingQuantity
                        : displaySq;
            }
            startingQuantities.push(displaySq);
        });
        runResults.push(targetResults);
        runResults.push([null]);
        runResults.push([null]);
        runResults.push(startingQuantities);
        runResults.push([null]);
        runResults.push([null]);
        runResults.push([null]);
        runResults.push([null]);
        return runResults;
    }
    else {
        return [
            [null],
            [null],
            [null],
            [null],
            [null],
            [null],
            [null],
            [null],
            [null],
            [null],
        ];
    }
}
exports.results = results;
/**
 * create the baseline data section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function baselineData(item, show) {
    let { targets, type } = item.run;
    let cycleNum = item.protocol.cycleNums;
    if (!show) {
        let response = [];
        let padding = MAX_TARGETS + 1;
        while (padding--) {
            response.push([null]);
        }
        return response;
    }
    let colors = [];
    let wellNumbers = [];
    targets.map((a) => {
        if (wellNumbers.indexOf(a.wellNumber) < 0) {
            wellNumbers.push(a.wellNumber);
        }
        if (colors.indexOf(a.color) < 0) {
            colors.push(a.color);
        }
    });
    let baselineSection = [];
    type === "Melt"
        ? baselineSection.push([
            "Negative First Derivative Relative Fluorescence (-d(RFU)/dT)",
            ...(targets[0].meltTemperatures || []).map((temperature) => {
                return {
                    v: temperature,
                    z: constants_1.PEAK_FORMAT,
                };
            }),
        ])
        : baselineSection.push([
            "Baseline Subtracted Relative Fluorescence (RFU)",
            ...targets[0].rawData.map((v, i) => i + 1),
        ]);
    for (let well of wellNumbers) {
        for (let color of colors) {
            let singleTargetData = [];
            let target = targets.find((target) => {
                return target.color === color && target.wellNumber === well;
            });
            if (target) {
                singleTargetData.push(`Well ${target.wellNumber + 1} ${target.color}`);
                for (let i = 0; i < cycleNum; i++) {
                    singleTargetData.push(target.baselineData[i]);
                }
                baselineSection.push(singleTargetData);
            }
        }
    }
    let targetCount = targets.length;
    let padding = MAX_TARGETS - targetCount;
    while (padding--) {
        baselineSection.push([null]);
    }
    return baselineSection;
}
exports.baselineData = baselineData;
/**
 * create the raw data section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function rawData(item, show) {
    let { targets, type } = item.run;
    let cycleNum = item.protocol.cycleNums;
    if (!show) {
        let response = [];
        let padding = MAX_TARGETS + 1;
        while (padding--) {
            response.push([null]);
        }
        return response;
    }
    let colors = [];
    let wellNumbers = [];
    targets.map((a) => {
        if (wellNumbers.indexOf(a.wellNumber) < 0) {
            wellNumbers.push(a.wellNumber);
        }
        if (colors.indexOf(a.color) < 0) {
            colors.push(a.color);
        }
    });
    // let rawDataSection: (string | number | null)[][] = [
    //   ["Raw Fluorescence (RFU)", ...targets[0].rawData.map((v, i) => i + 1)]
    // ];
    let rawDataSection = [];
    type === "Melt"
        ? rawDataSection.push([
            "Raw Fluorescence (RFU)",
            ...(targets[0].meltTemperatures || []).map((temperature) => {
                return {
                    v: temperature,
                    z: constants_1.PEAK_FORMAT,
                };
            }),
        ])
        : rawDataSection.push([
            "Raw Fluorescence (RFU)",
            ...targets[0].rawData.map((v, i) => i + 1),
        ]);
    for (let well of wellNumbers) {
        for (let color of colors) {
            let singleTargetData = [];
            let target = targets.find((target) => {
                return target.color === color && target.wellNumber === well;
            });
            if (target) {
                singleTargetData.push(`Well ${target.wellNumber + 1} ${target.color}`);
                for (let i = 0; i < cycleNum; i++) {
                    singleTargetData.push(target.rawData[i]);
                }
                rawDataSection.push(singleTargetData);
            }
        }
    }
    let targetCount = targets.length;
    let padding = MAX_TARGETS - targetCount;
    while (padding--) {
        rawDataSection.push([null]);
    }
    return rawDataSection;
}
exports.rawData = rawData;
/**
 * create the duration section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function duration(item) {
    let { run } = item;
    return [["Duration (MM:SS)", run.duration]];
}
exports.duration = duration;
/**
 * create the protocol section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function pcrProtocol(protocolSection, protocol) {
    protocolSection.push(["Cycles", protocol.cycleNums]);
    protocolSection.push([
        null,
        "Reverse Transcription",
        "Denature",
        "Cycling Denature",
        "Anneal",
        "Extension",
        protocol.isothermalInterval ? "Isothermal Interval" : null,
        protocol.isothermalTemp ? "Isothermal Temperature" : null,
    ]);
    protocolSection.push([
        "Temp (Celsius)",
        protocol.rtTemp,
        protocol.firstDenatureTemp,
        protocol.denatureTemp,
        protocol.annealTemp,
        protocol.extensionTemp,
        null,
        protocol.isothermalTemp,
    ]);
    protocolSection.push([
        "Time (s)",
        protocol.rtTime,
        protocol.firstDenatureTime,
        protocol.denatureTime,
        protocol.annealTime,
        protocol.extensionTime,
        protocol.isothermalInterval
            ? (0, utils_1.intervalToSamplesPerMin)(protocol.isothermalInterval || 0)
            : null,
    ]);
}
exports.pcrProtocol = pcrProtocol;
function meltProtocol(protocolSection, protocol) {
    protocolSection.push(["Steps", protocol.cycleNums]);
    protocolSection.push([
        null,
        "Melt Start Temp",
        "Melt End Temp",
        "Melt Increment",
        "Duration per step",
    ]);
    protocolSection.push([
        "Temp (Celsius)",
        protocol.meltTempStart,
        protocol.meltTempFinal,
        protocol.meltIncrement,
    ]);
    protocolSection.push(["Time (s) ", null, null, null, protocol.meltDuration]);
}
exports.meltProtocol = meltProtocol;
function protocol(item) {
    let { protocol, run } = item;
    let { type } = run;
    let protocolSection = [];
    protocolSection.push(["Protocol", protocol.name]);
    type === "Melt"
        ? meltProtocol(protocolSection, protocol)
        : pcrProtocol(protocolSection, protocol);
    return protocolSection;
}
exports.protocol = protocol;
/**
 * create the qr data section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function qrData(item) {
    let { run } = item;
    if (run.sku) {
        return [
            ["QR Data"],
            ["SKU", "Lot Number", "Expiration Date"],
            [run.sku, run.lotNumber, run.expirationDate],
        ];
    }
    else {
        return [[null], [null], [null]];
    }
}
exports.qrData = qrData;
/**
 * create the standard curve data section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function standard(item, runType) {
    const emptyStandard = {
        efficiencies: null,
        rSquares: null,
        yIntercepts: null,
        slopes: null,
    };
    let standardData = item.standardCurve;
    const standardGreen = standardData.green || emptyStandard;
    const standardAmber = standardData.amber || emptyStandard;
    const standardRed = standardData.red || emptyStandard;
    let showStandard = standardData &&
        (standardGreen.slopes || standardAmber.slopes || standardRed.slopes);
    let standardSection = [];
    standardSection.push(["Run Type", runType]);
    standardSection.push(["Standard Curve"]);
    standardSection.push([null, "Green", "Amber", "Red"]);
    standardSection.push([
        "E",
        standardGreen.efficiencies,
        standardAmber.efficiencies,
        standardRed.efficiencies,
    ]);
    standardSection.push([
        "R^2",
        standardGreen.rSquares,
        standardAmber.rSquares,
        standardRed.rSquares,
    ]);
    standardSection.push([
        "Y Intercept",
        standardGreen.yIntercepts,
        standardAmber.yIntercepts,
        standardRed.yIntercepts,
    ]);
    standardSection.push([
        "Slopes",
        standardGreen.slopes,
        standardAmber.slopes,
        standardRed.slopes,
    ]);
    return standardSection;
}
exports.standard = standard;
/**
 * create the location section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function location(item) {
    let { run } = item;
    return [
        ["Location", run.location],
        ["Location Tag", run.locationTag],
        ["Latitude", run.latitude],
        ["Longitude", run.longitude],
    ];
}
exports.location = location;
/**
 * create the notes section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function notes(item, show) {
    if (!show) {
        return [[null]];
    }
    let { run } = item;
    return [["Notes", run.notes]];
}
exports.notes = notes;
/**
 * create the optional section of the excel sheet, if one exists
 * @param item FormatOutput
 * @returns string[][]
 */
function optional(data) {
    let main = [];
    let others = [];
    const space = [null];
    const optDataKeys = Object.keys(data); //string array of object keys
    if (optDataKeys.length > 0) {
        optDataKeys.forEach((key) => {
            let value = data[key];
            switch (typeof value) {
                case "string": {
                    main.push([key, value]);
                    break;
                }
                case "number": {
                    main.push([key, value]);
                    break;
                }
                case "boolean": {
                    main.push([key, value]);
                    break;
                }
                case "object": {
                    if (Array.isArray(value)) {
                        main.push([key, ...value]);
                    }
                    else {
                        others.push(space, [key], ...optional(value));
                    }
                    break;
                }
                default: {
                    break;
                }
            }
        });
    }
    const extraSectionObjToArray = [...main, ...others];
    return extraSectionObjToArray;
}
exports.optional = optional;
/**
 * create the run version section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function version(item) {
    let systemInfo = item.run.version;
    let versionSection = [];
    versionSection.push([
        "HW Number",
        "SW Version",
        "BLE Name",
        "Mac Address",
        "Baseboard Version",
        "Baseboard Hardware",
        "Heater Version",
        "Heater Hardware",
        "Detector Version",
        "Detector Hardware",
    ]);
    versionSection.push([
        systemInfo.serialNumber,
        systemInfo.appVersion,
        systemInfo.bleName,
        systemInfo.macAddress,
        systemInfo.baseboardFw,
        systemInfo.baseboardHw,
        systemInfo.heaterFw,
        systemInfo.heaterHw,
        systemInfo.detectorFw,
        systemInfo.detectorHw,
    ]);
    return versionSection;
}
exports.version = version;
/**
 * create the xlsx version section of the excel sheet
 * @param item FormatOutput
 * @returns string[][]
 */
function xlsxVersion(item, version) {
    let { run, protocol } = item;
    let uuidHash = js_base64_1.Base64.encode(`runId: ${run.id},teamId: ${run.teamId},parentId: ${run.parentId},protocolId: ${protocol.id}`);
    let baselineSha = (0, js_sha256_1.sha256)(baselineData(item, true).reduce(function (a, b) {
        return a.concat(b);
    }, []));
    let rawDataSha = (0, js_sha256_1.sha256)(rawData(item, true).reduce(function (a, b) {
        return a.concat(b);
    }, []));
    return [
        ["Sheet Version", version],
        ["Hash", uuidHash],
        ["Baseline SHA256", baselineSha],
        ["Raw SHA256", rawDataSha],
    ];
}
exports.xlsxVersion = xlsxVersion;
/**
 * formats the run name to be a valid excel sheet name, determines if duplicate sheet names exist
 * @param item FormatOutput
 * @param sheetNames Object
 * @returns string
 */
function sheetName(item, sheetNames) {
    // remove invalid characters
    let name = "Run " + item.run.name.replace(/[\/:*?"<>|]/g, "-");
    /**
     * parse names down to 27 characters and check for duplicate names
     * if duplicate exists, set last 3 characters with (1) [total length now 30]
     **/
    let tmp = name.toUpperCase();
    sheetNames[tmp] = (sheetNames[tmp] || 0) + 1;
    let strIndex = sheetNames[tmp];
    if (name.length > 31) {
        let strSliced = name.slice(0, 27);
        if (strIndex > 1) {
            name = strSliced + `(${strIndex - 1})`;
        }
        else {
            name = strSliced;
        }
    }
    else {
        name = strIndex > 1 ? name + `(${strIndex - 1})` : name;
    }
    return name;
}
exports.sheetName = sheetName;
